import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { resolveUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut, Plus, Pencil, Trash2, Star, Mail, MailOpen, Save, Loader2, FolderKanban, Wrench, User, Settings as SettingsIcon, Inbox, FileText, Eye, EyeOff, Briefcase, GraduationCap, Upload, Users,
} from "lucide-react";
import { AdminDashboardSkeleton } from "@/components/Skeletons";
import { Seo } from "@/components/Seo";
import BrandLogo from "@/components/BrandLogo";

const TABS = [
  { id: "projects", label: "Projects", icon: FolderKanban },
  { id: "blogs", label: "Blog", icon: FileText },
  { id: "skills", label: "Skills", icon: Wrench },
  { id: "resume", label: "Resume Content", icon: Briefcase },
  { id: "profile", label: "Profile", icon: User },
  { id: "settings", label: "Settings", icon: SettingsIcon },
  { id: "messages", label: "Messages", icon: Inbox },
  { id: "leads", label: "Marketing Leads", icon: Users },
];

const emptyProject = {
  title: "", slug: "", summary: "", category: "Web App", cover_image: "", tech_stack: [], featured: false, order: 0,
  demo_url: "", repo_url: "", problem: "", goals: "", role: "", uiux: "", implementation: "",
  architecture: "", architecture_image: "", challenges: "", outcome: "", code_snippets: [], screenshots: [],
};

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm text-muted-foreground">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls = "w-full rounded-lg bg-background border border-border px-3 py-2 outline-none focus:ring-2 focus:ring-ring text-sm";

function FileUploader({ label, value, onChange, accept = "image/*", hideInput = false }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onChange(res.data.url);
      toast.success("File uploaded successfully");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadBtn = (
    <label className="bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border px-3 py-2 rounded-lg text-sm font-medium cursor-pointer inline-flex items-center gap-1.5 shrink-0 select-none">
      {uploading ? (
        <Loader2 className="w-4.5 h-4.5 animate-spin" />
      ) : (
        <Upload className="w-4.5 h-4.5" />
      )}
      <span>Upload</span>
      <input type="file" accept={accept} className="hidden" onChange={handleUpload} disabled={uploading} />
    </label>
  );

  if (hideInput) {
    return label ? <Field label={label}><div className="mt-1">{uploadBtn}</div></Field> : uploadBtn;
  }

  return (
    <Field label={label}>
      <div className="flex gap-2 items-center">
        <input className={inputCls + " flex-1"} value={value || ""} onChange={(e) => onChange(e.target.value)} />
        {uploadBtn}
      </div>
    </Field>
  );
}

function ProjectEditor({ initial, onClose, onSaved }) {
  const [p, setP] = useState({ ...emptyProject, ...initial, tech_stack: initial?.tech_stack || [], code_snippets: initial?.code_snippets || [], screenshots: initial?.screenshots || [] });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setP((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    if (!p.title) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = {
        ...p,
        order: Number(p.order) || 0,
        tech_stack: typeof p.tech_stack === "string" ? p.tech_stack.split(",").map((s) => s.trim()).filter(Boolean) : p.tech_stack,
        screenshots: typeof p.screenshots === "string" ? p.screenshots.split(",").map((s) => s.trim()).filter(Boolean) : p.screenshots,
      };
      if (initial?.id) await api.put(`/admin/projects/${initial.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("Project saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const updateSnippet = (i, key, val) => {
    setP((s) => {
      const cs = [...s.code_snippets];
      cs[i] = { ...cs[i], [key]: val };
      return { ...s, code_snippets: cs };
    });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm overflow-y-auto" data-testid="project-editor">
      <div className="min-h-screen px-4 py-10 flex justify-center">
        <div className="w-full max-w-3xl bg-card border border-border rounded-2xl p-6 sm:p-8 h-fit">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-heading">{initial?.id ? "Edit project" : "New project"}</h3>
            <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground" data-testid="editor-close">Close</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title"><input className={inputCls} value={p.title} onChange={set("title")} data-testid="proj-title-input" /></Field>
            <Field label="Slug (optional)"><input className={inputCls} value={p.slug} onChange={set("slug")} placeholder="auto-generated" /></Field>
            <Field label="Category"><input className={inputCls} value={p.category} onChange={set("category")} /></Field>
            <Field label="Order"><input className={inputCls} type="number" value={p.order} onChange={set("order")} /></Field>
            <FileUploader label="Cover image URL" value={p.cover_image} onChange={(url) => setP((s) => ({ ...s, cover_image: url }))} accept="image/*" />
            <Field label="Tech stack (comma separated)"><input className={inputCls} value={Array.isArray(p.tech_stack) ? p.tech_stack.join(", ") : p.tech_stack} onChange={set("tech_stack")} /></Field>
            <Field label="Demo URL"><input className={inputCls} value={p.demo_url} onChange={set("demo_url")} /></Field>
            <Field label="Repo URL"><input className={inputCls} value={p.repo_url} onChange={set("repo_url")} /></Field>
          </div>
          <div className="mt-4">
            <Field label="Summary"><textarea rows={2} className={inputCls} value={p.summary} onChange={set("summary")} data-testid="proj-summary-input" /></Field>
          </div>
          <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
            <input type="checkbox" checked={p.featured} onChange={(e) => setP((s) => ({ ...s, featured: e.target.checked }))} data-testid="proj-featured-checkbox" />
            Featured on homepage
          </label>

          <div className="mt-6 space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Case study</div>
            {[
              ["problem", "Problem"], ["goals", "Goals & Constraints"], ["role", "My Role"], ["uiux", "UI/UX Decisions"],
              ["implementation", "Key Implementation"], ["architecture", "Architecture"], ["challenges", "Challenges"], ["outcome", "Outcome"],
            ].map(([k, label]) => (
              <Field key={k} label={label}><textarea rows={3} className={inputCls} value={p[k]} onChange={set(k)} /></Field>
            ))}
            <FileUploader label="Architecture image URL" value={p.architecture_image} onChange={(url) => setP((s) => ({ ...s, architecture_image: url }))} accept="image/*" />
            <Field label="Screenshots (comma separated URLs)"><input className={inputCls} value={Array.isArray(p.screenshots) ? p.screenshots.join(", ") : p.screenshots} onChange={set("screenshots")} /></Field>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Code snippets</div>
              <button onClick={() => setP((s) => ({ ...s, code_snippets: [...s.code_snippets, { title: "", language: "javascript", code: "" }] }))} className="text-sm inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><Plus className="w-4 h-4" /> Add</button>
            </div>
            <div className="space-y-3">
              {p.code_snippets.map((s, i) => (
                <div key={i} className="border border-border rounded-lg p-3 space-y-2">
                  <div className="flex gap-2">
                    <input className={inputCls} placeholder="Title" value={s.title} onChange={(e) => updateSnippet(i, "title", e.target.value)} />
                    <input className={inputCls + " max-w-[160px]"} placeholder="Language" value={s.language} onChange={(e) => updateSnippet(i, "language", e.target.value)} />
                    <button onClick={() => setP((st) => ({ ...st, code_snippets: st.code_snippets.filter((_, idx) => idx !== i) }))} className="text-destructive px-2"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <textarea rows={4} className={inputCls + " font-mono"} placeholder="Code" value={s.code} onChange={(e) => updateSnippet(i, "code", e.target.value)} />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8">
            <button onClick={onClose} className="rounded-full px-5 py-2.5 text-sm glass">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="proj-save-button" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-projects-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading">Projects ({projects.length})</h2>
        <button onClick={() => setEditing(emptyProject)} data-testid="add-project-button" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium"><Plus className="w-4 h-4" /> Add project</button>
      </div>
      <div className="space-y-3">
        {projects.map((p) => (
          <div key={p.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4" data-testid={`admin-project-row-${p.slug}`}>
            <img src={resolveUrl(p.cover_image)} alt="" className="h-14 w-20 object-cover rounded-lg" />
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2">{p.title} {p.featured && <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />}</div>
              <div className="text-sm text-muted-foreground truncate">{p.summary}</div>
            </div>
            <button onClick={() => setEditing(p)} data-testid={`edit-project-${p.slug}`} className="h-9 w-9 grid place-items-center rounded-lg glass"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(p.id)} data-testid={`delete-project-${p.slug}`} className="h-9 w-9 grid place-items-center rounded-lg glass text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {editing && <ProjectEditor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function SkillsTab() {
  const [skills, setSkills] = useState([]);
  const [draft, setDraft] = useState({ category: "", items: "" });
  const load = () => api.get("/skills").then((r) => setSkills(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.category) return toast.error("Category required");
    await api.post("/admin/skills", { category: draft.category, items: draft.items.split(",").map((s) => s.trim()).filter(Boolean), order: skills.length });
    setDraft({ category: "", items: "" });
    toast.success("Added");
    load();
  };
  const update = async (g, items) => {
    await api.put(`/admin/skills/${g.id}`, { category: g.category, items, order: g.order });
    toast.success("Updated");
    load();
  };
  const remove = async (id) => {
    await api.delete(`/admin/skills/${id}`);
    load();
  };

  return (
    <div data-testid="admin-skills-tab">
      <h2 className="text-xl font-heading mb-6">Skill groups</h2>
      <div className="bg-card border border-border rounded-xl p-4 mb-6 grid sm:grid-cols-[1fr_2fr_auto] gap-3 items-end">
        <Field label="Category"><input className={inputCls} value={draft.category} onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))} data-testid="skill-category-input" /></Field>
        <Field label="Items (comma separated)"><input className={inputCls} value={draft.items} onChange={(e) => setDraft((d) => ({ ...d, items: e.target.value }))} data-testid="skill-items-input" /></Field>
        <button onClick={add} data-testid="add-skill-button" className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium">Add</button>
      </div>
      <div className="space-y-3">
        {skills.map((g) => (
          <div key={g.id} className="bg-card border border-border rounded-xl p-4" data-testid={`admin-skill-${g.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-medium">{g.category}</div>
              <button onClick={() => remove(g.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <input
              className={inputCls + " mt-2"}
              defaultValue={g.items.join(", ")}
              onBlur={(e) => update(g, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileTab() {
  const [p, setP] = useState(null);
  const [logos, setLogos] = useState([]);
  const loadLogos = () => api.get("/client-logos").then((r) => setLogos(r.data));
  
  useEffect(() => {
    api.get("/profile").then((r) => setP(r.data));
    loadLogos();
  }, []);
  const set = (k) => (e) => setP((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => { await api.put("/admin/profile", p); toast.success("Profile saved"); };
  if (!p) return null;

  const basicFields = [
    ["name", "Name"],
    ["role", "Role"],
    ["hero_kicker", "Hero kicker"],
    ["location", "Location"],
    ["email", "Email"],
    ["github", "GitHub URL"],
    ["linkedin", "LinkedIn URL"],
    ["twitter", "Twitter URL"]
  ];

  const statsFields = [
    ["years_experience", "Years experience stat (e.g. 5+)"],
    ["projects_count", "Projects count stat (e.g. 30+)"],
    ["happy_clients_count", "Happy clients stat (e.g. 10+)"],
    ["responsive_builds_rate", "Responsive builds rate (e.g. 100%)"],
    ["client_logos", "Client Logos Marquee (comma separated)"],
    ["about_status", "About Status Overlay text"]
  ];

  const resumeFields = [
    ["resume_last_updated", "Resume Last Updated Label (e.g. July 2026)"]
  ];

  return (
    <div data-testid="admin-profile-tab" className="space-y-8 max-w-4xl">
      <div>
        <h2 className="text-xl font-heading mb-6">Profile & homepage</h2>
        
        {/* Section: Basic Info */}
        <div className="border border-border/40 rounded-2xl p-6 bg-secondary/5 space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Basic Profile Information</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {basicFields.map(([k, l]) => (
              <Field key={k} label={l}>
                <input className={inputCls} value={p[k] || ""} onChange={set(k)} data-testid={`profile-${k}-input`} />
              </Field>
            ))}
            <FileUploader label="Avatar Photo" value={p.avatar} onChange={(url) => setP((s) => ({ ...s, avatar: url }))} accept="image/*" />
          </div>
        </div>
      </div>

      {/* Section: Homepage Media & Stats */}
      <div className="border border-border/40 rounded-2xl p-6 bg-secondary/5 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Homepage Stats & Media</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {statsFields.map(([k, l]) => (
            <Field key={k} label={l}>
              <input className={inputCls} value={p[k] || ""} onChange={set(k)} />
            </Field>
          ))}
          <FileUploader label="Profile Video" value={p.avatar_video} onChange={(url) => setP((s) => ({ ...s, avatar_video: url }))} accept="video/*" />
        </div>
      </div>

      {/* Section: About Pillars */}
      <div className="border border-border/40 rounded-2xl p-6 bg-secondary/5 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">About Page Pillars</h3>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Pillar 1 Title"><input className={inputCls} value={p.pillar1_title || ""} onChange={set("pillar1_title")} /></Field>
            <Field label="Pillar 1 Description"><input className={inputCls} value={p.pillar1_text || ""} onChange={set("pillar1_text")} /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 border-t border-border/10 pt-4">
            <Field label="Pillar 2 Title"><input className={inputCls} value={p.pillar2_title || ""} onChange={set("pillar2_title")} /></Field>
            <Field label="Pillar 2 Description"><input className={inputCls} value={p.pillar2_text || ""} onChange={set("pillar2_text")} /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 border-t border-border/10 pt-4">
            <Field label="Pillar 3 Title"><input className={inputCls} value={p.pillar3_title || ""} onChange={set("pillar3_title")} /></Field>
            <Field label="Pillar 3 Description"><input className={inputCls} value={p.pillar3_text || ""} onChange={set("pillar3_text")} /></Field>
          </div>
        </div>
      </div>

      {/* Section: Client Logos Marquee Settings */}
      <div className="border border-border/40 rounded-2xl p-6 bg-secondary/5 space-y-6">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Client Marquee Logos</h3>
          <p className="text-xs text-muted-foreground mt-1">Upload client brand logos and set their names to scroll on the homepage marquee.</p>
        </div>

        {/* Existing Logos Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {logos.map((logo, idx) => (
            <div key={logo.id || idx} className="glass p-4 rounded-xl relative flex flex-col justify-between items-center gap-3">
              {/* Delete button */}
              <button 
                onClick={async () => {
                  if (window.confirm("Delete this logo?")) {
                    await api.delete(`/admin/client-logos/${logo.id}`);
                    toast.success("Logo deleted");
                    loadLogos();
                  }
                }} 
                className="absolute top-2 right-2 text-muted-foreground hover:text-destructive transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>

              {/* Uploaded logo preview */}
              <div className="h-16 w-full flex items-center justify-center bg-secondary/20 rounded-lg overflow-hidden border border-border/40 p-2">
                {logo.logo_url ? (
                  <img
                    src={resolveUrl(logo.logo_url)}
                    alt={logo.name}
                    className="max-h-full max-w-full object-contain filter dark:invert"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      const parent = e.currentTarget.parentElement;
                      if (parent && !parent.querySelector(".fallback-brand")) {
                        const fallbackDiv = document.createElement("div");
                        fallbackDiv.className = "fallback-brand flex items-center justify-center";
                        fallbackDiv.innerText = logo.name.toUpperCase();
                        parent.appendChild(fallbackDiv);
                      }
                    }}
                  />
                ) : (
                  <BrandLogo name={logo.name} />
                )}
              </div>

              {/* Upload button */}
              <FileUploader 
                label="" 
                value={logo.logo_url} 
                onChange={async (newUrl) => {
                  await api.put(`/admin/client-logos/${logo.id}`, { ...logo, logo_url: newUrl });
                  loadLogos();
                }} 
                accept="image/*" 
                hideInput={true}
              />

              {/* Edit Name input */}
              <input 
                className={inputCls + " text-center font-bold text-xs uppercase tracking-wider"} 
                value={logo.name} 
                onChange={(e) => {
                  const updatedName = e.target.value;
                  setLogos(prev => prev.map(l => l.id === logo.id ? { ...l, name: updatedName } : l));
                }}
                onBlur={async (e) => {
                  await api.put(`/admin/client-logos/${logo.id}`, { ...logo, name: e.target.value });
                  toast.success("Name updated");
                }}
              />
            </div>
          ))}

          {/* Add Logo Box */}
          <div className="glass p-4 rounded-xl border-dashed border-2 border-border/60 flex flex-col justify-center items-center gap-2.5 min-h-[220px]">
            <div className="text-xs font-semibold text-muted-foreground uppercase">Add Client</div>
            <input 
              placeholder="Name (e.g. Stripe)" 
              className={inputCls + " text-center font-bold text-xs uppercase tracking-wider"} 
              id="new-logo-name" 
            />
            <button 
              onClick={async () => {
                const nameInput = document.getElementById("new-logo-name");
                const name = nameInput?.value?.trim();
                if (!name) return toast.error("Client name is required");
                
                await api.post("/admin/client-logos", {
                  name,
                  logo_url: "",
                  order: logos.length
                });
                
                if (nameInput) nameInput.value = "";
                toast.success("Client added! Now upload their logo image.");
                loadLogos();
              }} 
              className="bg-primary text-primary-foreground rounded-full px-4 py-2 text-xs font-semibold hover:opacity-90 inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add Client
            </button>
          </div>
        </div>
      </div>

      {/* Section: Resume & Tagline */}
      <div className="border border-border/40 rounded-2xl p-6 bg-secondary/5 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Resume & Bio Descriptions</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {resumeFields.map(([k, l]) => (
            <Field key={k} label={l}>
              <input className={inputCls} value={p[k] || ""} onChange={set(k)} />
            </Field>
          ))}
          <FileUploader label="Resume PDF Document" value={p.resume_url} onChange={(url) => setP((s) => ({ ...s, resume_url: url }))} accept=".pdf" />
        </div>
        <div className="mt-4 grid gap-4 border-t border-border/10 pt-4">
          <Field label="Tagline"><textarea rows={2} className={inputCls} value={p.tagline || ""} onChange={set("tagline")} /></Field>
          <Field label="Bio Description"><textarea rows={5} className={inputCls} value={p.bio || ""} onChange={set("bio")} /></Field>
        </div>
      </div>

      <button onClick={save} data-testid="save-profile-button" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-medium hover:opacity-90 transition-opacity">
        <Save className="w-4 h-4" /> Save profile
      </button>
    </div>
  );
}

function ExperienceEditor({ initial, onClose, onSaved }) {
  const empty = { role: "", org: "", period: "", points: [], order: 0 };
  const [exp, setExp] = useState({ ...empty, ...initial });
  const [pointsText, setPointsText] = useState((initial?.points || []).join("\n"));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!exp.role || !exp.org || !exp.period) {
      return toast.error("Role, Organization and Period are required");
    }
    setSaving(true);
    try {
      const payload = {
        ...exp,
        order: Number(exp.order) || 0,
        points: pointsText.split("\n").map(s => s.trim()).filter(Boolean)
      };
      if (initial?.id) {
        await api.put(`/admin/experiences/${initial.id}`, payload);
      } else {
        await api.post("/admin/experiences", payload);
      }
      toast.success("Work experience saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Failed to save experience");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-3xl p-6 shadow-2xl relative">
        <h3 className="text-lg font-heading font-medium mb-4">{initial?.id ? "Edit Experience" : "Add Work Experience"}</h3>
        <div className="space-y-4">
          <Field label="Role"><input className={inputCls} value={exp.role} onChange={(e) => setExp(s => ({ ...s, role: e.target.value }))} /></Field>
          <Field label="Organization"><input className={inputCls} value={exp.org} onChange={(e) => setExp(s => ({ ...s, org: e.target.value }))} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Period (e.g. 2023 — Present)"><input className={inputCls} value={exp.period} onChange={(e) => setExp(s => ({ ...s, period: e.target.value }))} /></Field>
            <Field label="Sort Order"><input type="number" className={inputCls} value={exp.order} onChange={(e) => setExp(s => ({ ...s, order: e.target.value }))} /></Field>
          </div>
          <Field label="Points / Description (One line per bullet point)">
            <textarea rows={5} className={inputCls} value={pointsText} onChange={(e) => setPointsText(e.target.value)} placeholder="Lead frontend developments...&#10;Built custom frameworks..." />
          </Field>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-primary text-primary-foreground rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

function EducationEditor({ initial, onClose, onSaved }) {
  const empty = { degree: "", school: "", period: "", order: 0 };
  const [edu, setEdu] = useState({ ...empty, ...initial });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!edu.degree || !edu.school || !edu.period) {
      return toast.error("Degree, School and Period are required");
    }
    setSaving(true);
    try {
      const payload = {
        ...edu,
        order: Number(edu.order) || 0
      };
      if (initial?.id) {
        await api.put(`/admin/educations/${initial.id}`, payload);
      } else {
        await api.post("/admin/educations", payload);
      }
      toast.success("Education entry saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Failed to save education");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-3xl p-6 shadow-2xl relative">
        <h3 className="text-lg font-heading font-medium mb-4">{initial?.id ? "Edit Education" : "Add Education Entry"}</h3>
        <div className="space-y-4">
          <Field label="Degree / Major"><input className={inputCls} value={edu.degree} onChange={(e) => setEdu(s => ({ ...s, degree: e.target.value }))} /></Field>
          <Field label="School / Institution"><input className={inputCls} value={edu.school} onChange={(e) => setEdu(s => ({ ...s, school: e.target.value }))} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Period (e.g. 2017 — 2021)"><input className={inputCls} value={edu.period} onChange={(e) => setEdu(s => ({ ...s, period: e.target.value }))} /></Field>
            <Field label="Sort Order"><input type="number" className={inputCls} value={edu.order} onChange={(e) => setEdu(s => ({ ...s, order: e.target.value }))} /></Field>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-primary text-primary-foreground rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ResumeTab() {
  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [activeExp, setActiveExp] = useState(null);
  const [activeEdu, setActiveEdu] = useState(null);

  const fetchContent = () => {
    setLoading(true);
    Promise.all([
      api.get("/experiences").then(r => setExperiences(r.data)),
      api.get("/educations").then(r => setEducations(r.data))
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const deleteExp = async (id) => {
    if (!window.confirm("Delete this experience entry?")) return;
    try {
      await api.delete(`/admin/experiences/${id}`);
      toast.success("Experience entry deleted");
      fetchContent();
    } catch (e) {
      toast.error("Failed to delete experience");
    }
  };

  const deleteEdu = async (id) => {
    if (!window.confirm("Delete this education entry?")) return;
    try {
      await api.delete(`/admin/educations/${id}`);
      toast.success("Education entry deleted");
      fetchContent();
    } catch (e) {
      toast.error("Failed to delete education");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="admin-resume-tab" className="space-y-12">
      {/* Experience Section */}
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-heading">Work Experience</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Manage work experience history displayed on the resume page.</p>
          </div>
          <button onClick={() => setActiveExp({})} className="bg-primary text-primary-foreground rounded-full px-4 py-2 text-xs font-semibold hover:opacity-90 inline-flex items-center gap-1.5 shrink-0">
            <Plus className="w-3.5 h-3.5" /> Add Experience
          </button>
        </div>

        <div className="grid gap-4">
          {experiences.map((e) => (
            <div key={e.id} className="glass p-5 rounded-2xl flex justify-between items-start gap-4">
              <div>
                <h4 className="font-heading font-semibold text-base">{e.role}</h4>
                <div className="text-sm text-muted-foreground mt-0.5">{e.org} • {e.period}</div>
                <div className="text-xs text-muted-foreground font-mono mt-1">Order: {e.order}</div>
                <ul className="mt-2.5 space-y-1">
                  {(e.points || []).map((p, idx) => (
                    <li key={idx} className="text-xs text-muted-foreground flex gap-1.5 leading-relaxed">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-foreground/30 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => setActiveExp(e)} className="p-1.5 hover:text-foreground text-muted-foreground hover:bg-secondary/40 rounded-lg transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => deleteExp(e.id)} className="p-1.5 hover:text-destructive text-muted-foreground hover:bg-secondary/40 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Education Section */}
      <div className="space-y-6 pt-6 border-t border-border/40">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-heading">Education History</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Manage academic credentials and graduation timeframes.</p>
          </div>
          <button onClick={() => setActiveEdu({})} className="bg-primary text-primary-foreground rounded-full px-4 py-2 text-xs font-semibold hover:opacity-90 inline-flex items-center gap-1.5 shrink-0">
            <Plus className="w-3.5 h-3.5" /> Add Education
          </button>
        </div>

        <div className="grid gap-4">
          {educations.map((ed) => (
            <div key={ed.id} className="glass p-5 rounded-2xl flex justify-between items-start gap-4">
              <div>
                <h4 className="font-heading font-semibold text-base">{ed.degree}</h4>
                <div className="text-sm text-muted-foreground mt-0.5">{ed.school} • {ed.period}</div>
                <div className="text-xs text-muted-foreground font-mono mt-1">Order: {ed.order}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => setActiveEdu(ed)} className="p-1.5 hover:text-foreground text-muted-foreground hover:bg-secondary/40 rounded-lg transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => deleteEdu(ed.id)} className="p-1.5 hover:text-destructive text-muted-foreground hover:bg-secondary/40 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      {activeExp && <ExperienceEditor initial={activeExp} onClose={() => setActiveExp(null)} onSaved={fetchContent} />}
      {activeEdu && <EducationEditor initial={activeEdu} onClose={() => setActiveEdu(null)} onSaved={fetchContent} />}
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const set = (k) => (e) => setS((v) => ({ ...v, [k]: e.target.value }));
  const save = async () => { await api.put("/admin/settings", s); toast.success("Settings saved"); };
  if (!s) return null;
  return (
    <div data-testid="admin-settings-tab">
      <h2 className="text-xl font-heading mb-6">Booking & contact settings</h2>
      <div className="grid gap-4 max-w-2xl">
        <Field label="Zoho Calendar booking URL"><input className={inputCls} value={s.booking_url || ""} onChange={set("booking_url")} data-testid="booking-url-input" placeholder="https://calendar.zoho.com/..." /></Field>
        <Field label="Booking heading"><input className={inputCls} value={s.booking_heading || ""} onChange={set("booking_heading")} /></Field>
        <Field label="Booking subtext"><textarea rows={2} className={inputCls} value={s.booking_subtext || ""} onChange={set("booking_subtext")} /></Field>
        <Field label="Contact email"><input className={inputCls} value={s.contact_email || ""} onChange={set("contact_email")} /></Field>
        <Field label="Resume URL"><input className={inputCls} value={s.resume_url || ""} onChange={set("resume_url")} /></Field>
      </div>
      <button onClick={save} data-testid="save-settings-button" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium"><Save className="w-4 h-4" /> Save settings</button>
    </div>
  );
}

function BlogEditor({ initial, onClose, onSaved }) {
  const empty = { title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: [], author: "Sandeep Varikuppala", published: true, read_time: "", meta_description: "" };
  const [b, setB] = useState({ ...empty, ...initial, tags: initial?.tags || [] });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setB((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    if (!b.title) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = { ...b, tags: typeof b.tags === "string" ? b.tags.split(",").map((s) => s.trim()).filter(Boolean) : b.tags };
      if (initial?.id) await api.put(`/admin/blogs/${initial.id}`, payload);
      else await api.post("/admin/blogs", payload);
      toast.success("Post saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm overflow-y-auto" data-testid="blog-editor">
      <div className="min-h-screen px-4 py-10 flex justify-center">
        <div className="w-full max-w-3xl bg-card border border-border rounded-2xl p-6 sm:p-8 h-fit">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-heading">{initial?.id ? "Edit post" : "New post"}</h3>
            <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground" data-testid="blog-editor-close">Close</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title"><input className={inputCls} value={b.title} onChange={set("title")} data-testid="blog-title-input" /></Field>
            <Field label="Slug (optional)"><input className={inputCls} value={b.slug} onChange={set("slug")} placeholder="auto-generated" /></Field>
            <FileUploader label="Cover image URL" value={b.cover_image} onChange={(url) => setB((s) => ({ ...s, cover_image: url }))} accept="image/*" />
            <Field label="Read time"><input className={inputCls} value={b.read_time} onChange={set("read_time")} placeholder="6 min read" /></Field>
            <Field label="Author"><input className={inputCls} value={b.author} onChange={set("author")} /></Field>
            <Field label="Tags (comma separated)"><input className={inputCls} value={Array.isArray(b.tags) ? b.tags.join(", ") : b.tags} onChange={set("tags")} /></Field>
          </div>
          <div className="mt-4 space-y-4">
            <Field label="Excerpt"><textarea rows={2} className={inputCls} value={b.excerpt} onChange={set("excerpt")} data-testid="blog-excerpt-input" /></Field>
            <Field label="Meta description (SEO)"><textarea rows={2} className={inputCls} value={b.meta_description} onChange={set("meta_description")} /></Field>
            <Field label="Content (Markdown supported)"><textarea rows={12} className={inputCls + " font-mono"} value={b.content} onChange={set("content")} data-testid="blog-content-input" placeholder="## Heading\n\nWrite your post in markdown..." /></Field>
          </div>
          <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
            <input type="checkbox" checked={b.published} onChange={(e) => setB((s) => ({ ...s, published: e.target.checked }))} data-testid="blog-published-checkbox" />
            Published (visible on the public blog)
          </label>
          <div className="flex justify-end gap-3 mt-8">
            <button onClick={onClose} className="rounded-full px-5 py-2.5 text-sm glass">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="blog-save-button" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/blogs").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blogs/${id}`);
    toast.success("Deleted");
    load();
  };
  return (
    <div data-testid="admin-blogs-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading">Blog posts ({posts.length})</h2>
        <button onClick={() => setEditing({})} data-testid="add-blog-button" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium"><Plus className="w-4 h-4" /> New post</button>
      </div>
      <div className="space-y-3">
        {posts.length === 0 && <p className="text-muted-foreground">No posts yet.</p>}
        {posts.map((p) => (
          <div key={p.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4" data-testid={`admin-blog-row-${p.slug}`}>
            {p.cover_image && <img src={resolveUrl(p.cover_image)} alt="" className="h-14 w-20 object-cover rounded-lg" />}
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2">{p.title} {p.published ? <Eye className="w-3.5 h-3.5 text-green-500" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}</div>
              <div className="text-sm text-muted-foreground truncate">{p.excerpt}</div>
            </div>
            <button onClick={() => setEditing(p)} data-testid={`edit-blog-${p.slug}`} className="h-9 w-9 grid place-items-center rounded-lg glass"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(p.id)} data-testid={`delete-blog-${p.slug}`} className="h-9 w-9 grid place-items-center rounded-lg glass text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {editing && <BlogEditor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/leads")
      .then((r) => setLeads(r.data))
      .catch((err) => toast.error("Failed to load leads"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted successfully");
      load();
    } catch (err) {
      toast.error("Failed to delete lead");
    }
  };

  return (
    <div data-testid="admin-leads-tab">
      <h2 className="text-xl font-heading mb-6">Captured Marketing Leads ({leads.length})</h2>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-4">
          {leads.length === 0 && <p className="text-muted-foreground">No leads captured yet.</p>}
          {leads.map((l) => (
            <div key={l.id} className="bg-card border border-border rounded-xl p-6" data-testid={`lead-${l.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="font-semibold text-base">{l.name}</div>
                  <div className="text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                    <span>Email: {l.email}</span>
                    <span>Phone: {l.phone}</span>
                  </div>
                  <div className="text-sm font-medium text-primary mt-1">
                    Requested Service: {l.service || "General Inquiry"}
                  </div>
                </div>
                <button onClick={() => remove(l.id)} className="h-9 w-9 grid place-items-center rounded-lg glass text-destructive shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>

              {/* Campaign / UTM Details */}
              <div className="mt-4 pt-4 border-t border-border/60 grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs bg-secondary/50 p-3.5 rounded-xl">
                <div>
                  <span className="block text-muted-foreground font-mono text-[10px] uppercase">Source</span>
                  <span className="font-medium">{l.utm_source || "—"}</span>
                </div>
                <div>
                  <span className="block text-muted-foreground font-mono text-[10px] uppercase">Medium</span>
                  <span className="font-medium">{l.utm_medium || "—"}</span>
                </div>
                <div>
                  <span className="block text-muted-foreground font-mono text-[10px] uppercase">Campaign</span>
                  <span className="font-medium">{l.utm_campaign || "—"}</span>
                </div>
                <div>
                  <span className="block text-muted-foreground font-mono text-[10px] uppercase">Content</span>
                  <span className="font-medium">{l.utm_content || "—"}</span>
                </div>
                <div>
                  <span className="block text-muted-foreground font-mono text-[10px] uppercase">Term</span>
                  <span className="font-medium">{l.utm_term || "—"}</span>
                </div>
              </div>

              {/* Source Page Info */}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground font-light">
                <span className="truncate max-w-md">Source URL: <a href={l.source_url} target="_blank" rel="noreferrer" className="underline hover:text-primary">{l.source_url || "—"}</a></span>
                <span>{new Date(l.created_at).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessagesTab() {
  const [msgs, setMsgs] = useState([]);
  const load = () => api.get("/admin/contacts").then((r) => setMsgs(r.data));
  useEffect(() => { load(); }, []);
  const toggleRead = async (m) => { await api.put(`/admin/contacts/${m.id}/read`); load(); };
  const remove = async (id) => { await api.delete(`/admin/contacts/${id}`); load(); };
  return (
    <div data-testid="admin-messages-tab">
      <h2 className="text-xl font-heading mb-6">Contact submissions ({msgs.length})</h2>
      <div className="space-y-3">
        {msgs.length === 0 && <p className="text-muted-foreground">No messages yet.</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`bg-card border rounded-xl p-4 ${m.read ? "border-border" : "border-primary/40"}`} data-testid={`message-${m.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium">{m.name} <span className="text-muted-foreground font-normal">· {m.email}</span></div>
                {m.subject && <div className="text-sm text-muted-foreground">{m.subject}</div>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => toggleRead(m)} className="h-9 w-9 grid place-items-center rounded-lg glass" title="Mark read">{m.read ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}</button>
                <button onClick={() => remove(m.id)} className="h-9 w-9 grid place-items-center rounded-lg glass text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{m.message}</p>
            <div className="text-xs text-muted-foreground mt-2">{new Date(m.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [user, loading, navigate]);

  if (loading || !user) return <AdminDashboardSkeleton />;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 py-6" data-testid="admin-dashboard">
      <Seo title="Admin Dashboard" noIndex path="/admin" />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-heading">Admin Dashboard</h1>
          <p className="text-sm text-muted-foreground">Signed in as {user.email}</p>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-button" className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm"><LogOut className="w-4 h-4" /> Logout</button>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-8 min-w-0 w-full">
        <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 scrollbar-none max-w-full">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm whitespace-nowrap transition-colors ${tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        <div className="min-w-0">
          {tab === "projects" && <ProjectsTab />}
          {tab === "blogs" && <BlogTab />}
          {tab === "skills" && <SkillsTab />}
          {tab === "resume" && <ResumeTab />}
          {tab === "profile" && <ProfileTab />}
          {tab === "settings" && <SettingsTab />}
          {tab === "messages" && <MessagesTab />}
          {tab === "leads" && <LeadsTab />}
        </div>
      </div>
    </div>
  );
}
